import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  Card,
  Button,
  Avatar,
} from "@mui/material";

import GroupsIcon from "@mui/icons-material/Groups";
import ImportExportIcon from "@mui/icons-material/ImportExport";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ArrowDropUpRoundedIcon from "@mui/icons-material/ArrowDropUpRounded";

import TeacherNavbar from "../components/TeacherNavbar";
import TeacherGroupView from "../components/TeacherGroupView";
import TeacherNewGroupForm from "../components/TeacherNewGroupForm";

import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebaseConfig";
import { collection, getDocs, query, where, documentId } from "firebase/firestore";

import { SUBJECTS } from "../constants";
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { useTranslation } from "react-i18next";

export default function TeacherGroups() {
  const { t } = useTranslation();
  const [groupModalOpen, setGroupModalOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [teacherDocId, setTeacherDocId] = useState(null);
  const [teachers, setTeachers] = useState([]); // will contain ONLY current teacher
  const [groups, setGroups] = useState([]);

  // ----- 1) Get current teacher doc + teacher groups -----
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      try {
        setLoading(true);

        if (!user) {
          setTeacherDocId(null);
          setTeachers([]);
          setGroups([]);
          return;
        }

        // find teacher doc by uid
        const tq = query(collection(db, "teachers"), where("uid", "==", user.uid));
        const tsnap = await getDocs(tq);

        if (tsnap.empty) {
          setTeacherDocId(null);
          setTeachers([]);
          setGroups([]);
          return;
        }

        const tDoc = tsnap.docs[0];
        const tDocId = tDoc.id;
        const tData = tDoc.data() || {};

        setTeacherDocId(tDocId);

        // IMPORTANT: pass teachers[] containing only current teacher
        setTeachers([
          {
            id: tDocId,
            firstName: tData.firstName || "",
            lastName: tData.lastName || "",
            subjectIds: tData.subjectIds || [],
          },
        ]);

        // load ONLY groups of this teacher using teacher.groupIds (source of truth)
        const groupIds = Array.isArray(tData.groupIds) ? tData.groupIds.filter(Boolean) : [];

        if (!groupIds.length) {
        setGroups([]);
        } else {
        // Firestore "in" supports max 10 values => batch
        const chunks = [];
        for (let i = 0; i < groupIds.length; i += 10) {
            chunks.push(groupIds.slice(i, i + 10));
        }

        const groupDocs = await Promise.all(
            chunks.map((chunk) => {
            const qGroups = query(
                collection(db, "groups"),
                where(documentId(), "in", chunk)
            );
            return getDocs(qGroups);
            })
        );

        const merged = groupDocs.flatMap((snap) =>
            snap.docs.map((d) => ({ id: d.id, ...d.data() }))
        );

        // de-dupe (safety)
        const uniq = Array.from(new Map(merged.map((g) => [g.id, g])).values());

        setGroups(uniq);
        }
      } catch (e) {
        console.error("TeacherGroups load error:", e);
        setTeacherDocId(null);
        setTeachers([]);
        setGroups([]);
      } finally {
        setLoading(false);
      }
    });

    return () => unsub();
  }, []);

  // ----- 2) Restrict LEVELS + SUBJECTS to only what the teacher is assigned -----
  // We keep the UI identical, we just pass filtered arrays to NewGroupForm.
  const teacherSubjectIds = teachers?.[0]?.subjectIds || [];

  const allowedSubjects = useMemo(() => {
    if (!teacherSubjectIds.length) return [];
    return SUBJECTS.filter((s) => teacherSubjectIds.includes(s.id));
  }, [teacherSubjectIds]);

  const allowedLevels = useMemo(() => {
    const levels = allowedSubjects.map((s) => s.level).filter(Boolean);
    return [...new Set(levels)];
  }, [allowedSubjects]);

  // ----- 3) Analytics (simple, same style block) -----
  const totalGroups = groups.length;

  const now = useMemo(() => new Date(), []);
  const last7Days = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d;
  }, []);

  const newGroups7d = groups.filter((g) => {
    if (!g.entryDate) return false;
    return new Date(g.entryDate) >= last7Days;
  }).length;

  return (
    <div
      style={{
        background:
          'radial-gradient(1200px 600px at 10% -10%, rgba(255, 204, 155, 0.35), transparent 60%), radial-gradient(900px 500px at 100% 0%, rgba(130, 189, 255, 0.35), transparent 60%), #f6f7fb',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: '"Space Grotesk", "Work Sans", system-ui, sans-serif',
        overflowX: 'hidden',
        width: '100%',
        boxSizing: 'border-box',
      }}
    >
      <TeacherNavbar />

      <Box
        sx={{
          display: "flex",
          height: "calc(100vh - 64px)",
          backgroundColor: "transparent",
          flexDirection: "row-reverse",
          overflow: "hidden",
        }}
      >
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 1, md: 2 },
            overflowY: "auto",
            direction: "rtl",
          }}
        >
          {/* Header card (Groups only) */}
          <Card
            elevation={0}
            sx={{
              mb: 2.5,
              borderRadius: 4,
              px: 3,
              py: 2.5,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              background: "linear-gradient(180deg, #ffffff 0%, #f9fafb 100%)",
              boxShadow: "0 10px 30px rgba(0,0,0,0.06)",
              border: "1px solid rgba(0,0,0,0.06)",
            }}
          >

            {/* RIGHT SIDE — TITLE + SUBTITLE + ICON */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar
                sx={{
                    bgcolor: "#eef2ff",
                    color: "#4f46e5",
                    width: 46,
                    height: 46,
                    boxShadow: "0 8px 18px rgba(79,70,229,0.18)",
                }}
                >
                <GroupsOutlinedIcon />
                </Avatar>

                <Box sx={{ textAlign: "right" }}>
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {t("الأقسام")}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {t("إدارة الأقسام الخاصة بك داخل المنصة")}
                </Typography>
                </Box>
            </Box>

            {/* LEFT SIDE — BUTTONS */}
            <Box sx={{ display: "flex", gap: 1, direction: "ltr" }}>
              <Button variant="outlined" size="small">
                <ImportExportIcon /> {t("تصدير")}
              </Button>

              <Button
                variant="contained"
                size="small"
                onClick={() => setGroupModalOpen(true)}
                startIcon={<GroupsIcon />}
                disabled={loading || !teacherDocId}
              >
                {t("فتح قسم جديد")}
              </Button>
            </Box>
          </Card>

          {/* Small analytics card (same vibe) */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(2, 1fr)" },
              gap: 2,
              mb: 3,
            }}
          >
            {[
              {
                title: t("إجمالي الأقسام"),
                value: totalGroups,
                deltaText: t("TBC% الأسبوع الماضي"),
                icon: <GroupsIcon sx={{ fontSize: 20, opacity: 0.9 }} />,
              },
              {
                title: t("الأقسام الجديدة (7 أيام)"),
                value: newGroups7d,
                deltaText: t("TBC% الأسبوع الماضي"),
                icon: <TrendingUpIcon sx={{ fontSize: 20, opacity: 0.9 }} />,
              },
            ].map((item, idx) => (
              <Card
                key={idx}
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: "1px solid rgba(0,0,0,0.08)",
                  backgroundColor: "#fff",
                  boxShadow: "0 6px 18px rgba(0,0,0,0.05)",
                  px: 2.25,
                  py: 2,
                  overflow: "hidden",
                  minHeight: 124,
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: "rgba(0,0,0,0.55)" }}>
                    {item.title}
                  </Typography>

                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: 2,
                      display: "grid",
                      placeItems: "center",
                      backgroundColor: "rgba(0,0,0,0.04)",
                      border: "1px solid rgba(0,0,0,0.06)",
                    }}
                  >
                    {item.icon}
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                  <Typography sx={{ fontSize: 40, fontWeight: 900, lineHeight: 1 }}>
                    {item.value}
                  </Typography>
                  <Typography sx={{ fontSize: 30, fontWeight: 900, color: "rgba(0,0,0,0.25)" }}>
                    -
                  </Typography>
                </Box>

                <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                      backgroundColor: "rgba(34,197,94,0.12)",
                      color: "rgba(22,163,74,0.95)",
                    }}
                  >
                    <ArrowDropUpRoundedIcon sx={{ fontSize: 20 }} />
                  </Box>

                  <Typography sx={{ fontSize: 13, color: "rgba(0,0,0,0.55)", fontWeight: 600 }}>
                    {item.deltaText}
                  </Typography>
                </Box>
              </Card>
            ))}
          </Box>

          {/* Groups view container (same container style) */}
          <Card
            elevation={0}
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              backgroundColor: "#ffffff",
              boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
              border: "1px solid rgba(0,0,0,0.06)",
            }}
          >
            <Box>
                <TeacherGroupView
                groups={groups}
                teachers={teachers}
                students={students}
                setGroups={setGroups}
                setStudents={setStudents}
                />
            </Box>
          </Card>

          {/* Group modal (same UI + same NewGroupForm) */}
          <Dialog
            open={groupModalOpen}
            onClose={() => setGroupModalOpen(false)}
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                overflow: "hidden",
                boxShadow: "0 20px 60px rgba(15, 23, 42, 0.18)",
              },
            }}
          >
            <DialogTitle
              align="center"
              sx={{
                direction: "rtl",
                fontWeight: 800,
                fontSize: 22,
                py: 2,
                borderBottom: 1,
                borderColor: "divider",
                letterSpacing: "0.2px",
              }}
            >
              {t("إضافة مجموعة (قسم) جديدة")}
            </DialogTitle>

            <DialogContent
              sx={{
                direction: "rtl",
                p: { xs: 2, md: 3 },
                bgcolor: "background.default",
                "& .MuiOutlinedInput-root": { borderRadius: 2 },
                "& .MuiInputBase-root": { borderRadius: 2 },
                "& .MuiButton-containedPrimary": {
                  borderRadius: 2,
                  bgcolor: "primary.main",
                  "&:hover": { bgcolor: "primary.dark" },
                },
                "& .MuiButton-outlinedPrimary": { borderRadius: 2 },
              }}
            >
              <TeacherNewGroupForm
                onClose={() => setGroupModalOpen(false)}
                setGroups={setGroups}
                teacherDocId={teacherDocId}
                levels={allowedLevels}
                subjects={allowedSubjects}
              />
            </DialogContent>
          </Dialog>
        </Box>
      </Box>
    </div>
  );
}
